import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import staffAttendanceService from '../services/staffAttendanceService';
import { Clock, CheckCircle } from '@phosphor-icons/react';

/**
 * Self-service clock-in/out button. Used both embedded in the teacher
 * portal (no qrToken — works when the school's mode is "open") and on the
 * dedicated /staff-clock-in landing page reached by scanning the gate QR
 * code (qrToken comes from the scanned URL's query string).
 */
function ClockInOutWidget({ qrToken = null }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const loadStatus = () => {
    setLoading(true);
    staffAttendanceService.getTodayStatus()
      .then((data) => setStatus(data))
      .catch((err) => toast.error(err.message || 'Unable to load attendance status'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadStatus(); }, []);

  const handleClockIn = async () => {
    try {
      setSubmitting(true);
      const result = await staffAttendanceService.clockIn(qrToken);
      toast.success(`Clocked in at ${result.check_in}`);
      if (result.ip_flagged) {
        toast.warning('Note: this was logged from an unrecognized network — HR may follow up.');
      }
      loadStatus();
    } catch (error) {
      toast.error(error.message || 'Unable to clock in');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClockOut = async () => {
    try {
      setSubmitting(true);
      const result = await staffAttendanceService.clockOut(qrToken);
      toast.success(`Clocked out at ${result.check_out}`);
      loadStatus();
    } catch (error) {
      toast.error(error.message || 'Unable to clock out');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 16, textAlign: 'center', color: '#6b7280' }}>Loading...</div>;
  }

  if (!status) return null;

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 20, textAlign: 'center', background: '#fff' }}>
      <Clock size={28} color="#6366f1" style={{ marginBottom: 8 }} />
      {!status.clocked_in ? (
        <>
          <div style={{ fontSize: 14, color: '#374151', marginBottom: 12 }}>You haven't clocked in today.</div>
          {status.clock_in_mode === 'qr_required' && !qrToken && (
            <div style={{ fontSize: 12, color: '#d97706', marginBottom: 10 }}>
              This school requires scanning the QR code at the entrance to clock in.
            </div>
          )}
          <button className="btn btn-primary" onClick={handleClockIn} disabled={submitting}>
            {submitting ? 'Clocking in...' : 'Clock In'}
          </button>
        </>
      ) : !status.clocked_out ? (
        <>
          <div style={{ fontSize: 14, color: '#15803d', marginBottom: 4 }}>
            <CheckCircle size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Clocked in at {status.check_in}
          </div>
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>Don't forget to clock out when you leave.</div>
          <button className="btn btn-secondary" onClick={handleClockOut} disabled={submitting}>
            {submitting ? 'Clocking out...' : 'Clock Out'}
          </button>
        </>
      ) : (
        <div style={{ fontSize: 14, color: '#15803d' }}>
          <CheckCircle size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Done for today — in at {status.check_in}, out at {status.check_out}
        </div>
      )}
    </div>
  );
}

export default ClockInOutWidget;
